import React, { FC, memo, useCallback, useEffect, useMemo } from 'react';
import { Form } from 'antd';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Button } from 'shared/ui/Button';
import { TextArea } from 'shared/ui/TextArea';
import { Checkbox } from 'shared/ui/Checkbox';
import { showNotification } from 'app/providers/NotificationsProvider';
import {
  CreateWaitingListEntryData,
  WaitingListEntry,
  useCreateWaitingListEntryMutation,
  useUpdateWaitingListEntryMutation,
} from 'entities/WaitingListEntry';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker } from 'shared/ui/DatePicker';
import { CLIENT_DATE_FORMAT } from 'shared/utils/constants';
import { Paragraph } from 'shared/ui/Paragraph';
import { DrawerMode } from '../model/types';
import { Select } from 'shared/ui/Select';
import { useGetAllWarehousesQuery } from 'entities/Warehouse';
import { getLoggedUserData, getUserName, User } from 'entities/User';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { Lead } from 'entities/Lead';
import { chooseUserModalActions, getSelectedUser, UserType } from 'widgets/ChooseUserModal';
import { Input } from 'shared/ui/Input';
import { UnitType } from 'entities/Contract';
import { MultipleSelect } from 'shared/ui/MultipleSelect';
import { getServerDateFormat } from 'shared/utils/helpers/getDateFormat';
import { RangeInput } from 'shared/ui/RangeInput/RangeInput';

interface WaitingListEntryFormProps {
  mode: Nullable<DrawerMode>;
  onClose: () => void;
  waitingListEntry?: Nullable<WaitingListEntry>;
  lead?: Nullable<Lead>;
  user?: Nullable<User>;
}

export const WaitingListEntryForm: FC<WaitingListEntryFormProps> = memo((props) => {
  const { waitingListEntry, mode, lead, user, onClose } = props;
  const { t } = useAppTranslation(['contracts', 'common']);

  const [form] = Form.useForm();
  const watchedSquare = Form.useWatch('square', form);

  const initialSquare = useMemo(
    () => [waitingListEntry?.squareFrom ?? null, waitingListEntry?.squareTo ?? null],
    [waitingListEntry?.squareFrom, waitingListEntry?.squareTo],
  );

  const square = watchedSquare ?? initialSquare;

  const [createWaitingListEntry, { isLoading: isCreationLoading }] = useCreateWaitingListEntryMutation();
  const [updateWaitingListEntry, { isLoading: isUpdationLoading }] = useUpdateWaitingListEntryMutation();
  const { data: warehouses } = useGetAllWarehousesQuery();

  const loggedUser = useAppSelector(getLoggedUserData);

  const selectedUser = useAppSelector(getSelectedUser);

  const isLoading = isUpdationLoading || isCreationLoading;

  const isEditMode = mode === DrawerMode.EDIT;

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (waitingListEntry) {
      form.setFieldsValue({
        ...waitingListEntry,
        description: null,
        userType: waitingListEntry.lead ? UserType.LEAD : waitingListEntry.user ? UserType.USER : null,
        preferredDate: waitingListEntry?.preferredDate && dayjs(waitingListEntry.preferredDate),
      });

      dispatch(chooseUserModalActions.setSelectedUser(waitingListEntry.lead || waitingListEntry.user));
    } else {
      form.resetFields();
    }

    if (lead) {
      form.setFieldsValue({
        userType: UserType.LEAD,
        description: `
        Name: ${lead.firstName || ''} ${lead.lastName || ''} 
        Email: ${lead.email || '-'}
        Phone: ${lead.phone || '-'}
        `,
      });

      dispatch(chooseUserModalActions.setSelectedUser(lead));
    }

    if (user) {
      form.setFieldsValue({
        userType: UserType.USER,
        description: `
        Name: ${getUserName(user)} 
        Email: ${user.email}
        Phone: ${user.phone}
        `,
      });

      dispatch(chooseUserModalActions.setSelectedUser(user));
    }
  }, [dispatch, form, lead, waitingListEntry, user]);

  const handleSubmit = useCallback(
    async (data: CreateWaitingListEntryData): Promise<void> => {
      const waitingListEntryData = { ...data };

      if (selectedUser && (!lead || !user)) {
        if ('leadId' in selectedUser) waitingListEntryData.leadId = selectedUser.leadId;

        if ('userId' in selectedUser && selectedUser.userId) waitingListEntryData.userId = selectedUser.userId;
      }

      try {
        switch (mode) {
          case DrawerMode.CREATE:
            await createWaitingListEntry({
              ...waitingListEntryData,
              preferredDate: data?.preferredDate ? getServerDateFormat(data.preferredDate) : null,
              squareFrom: square?.[0],
              squareTo: square?.[1],
            }).unwrap();
            showNotification('info', t('Success', { ns: 'common' }), t('Waiting list has been successfully updated'));
            break;
          case DrawerMode.EDIT:
            if (waitingListEntry) {
              const { waitingListEntryId } = waitingListEntry;
              await updateWaitingListEntry({
                ...waitingListEntryData,
                waitingListEntryId,
                preferredDate: data?.preferredDate ? getServerDateFormat(data.preferredDate) : null,
                squareFrom: square?.[0],
                squareTo: square?.[1],
                description: `
                ${t('Changed by')} ${getUserName(loggedUser)}
                ${data.description || ''}
                `,
              }).unwrap();
              showNotification('info', t('Success', { ns: 'common' }), t('Waiting list has been successfully updated'));
            }
            break;
        }

        form.resetFields();

        onClose();
      } catch (error: CustomAny) {
        console.log(error);
        showNotification('error', t('Error', { ns: 'common' }), t('Error when updating waiting list'));
      }
    },
    [
      selectedUser,
      lead,
      user,
      mode,
      form,
      onClose,
      createWaitingListEntry,
      square,
      t,
      waitingListEntry,
      updateWaitingListEntry,
      loggedUser,
    ],
  );

  const disableDate = useCallback((currentDate: Dayjs): boolean => {
    const today = dayjs().startOf('day');

    return currentDate.isBefore(today, 'day');
  }, []);

  const paragraphs = waitingListEntry?.description.split('\n');

  const paragraphElements = paragraphs?.map((paragraph: string, index: number) => (
    <p key={index} className="font-normal !mb-1">
      {paragraph}
    </p>
  ));

  const warehouseOptions = useMemo(() => {
    return (
      warehouses?.map((warehouse) => ({
        label: warehouse.name,
        value: warehouse.warehouseId,
      })) || []
    );
  }, [warehouses]);

  const userTypeOptions = useMemo(
    () => [
      { label: t('Lead'), value: UserType.LEAD },
      { label: t('Customer'), value: UserType.USER },
    ],
    [t],
  );

  const unitTypeOptions = Object.values(UnitType).map((type) => ({
    label: t(type),
    value: type,
  }));

  const changeUserType = useCallback(
    (value: string): void => {
      dispatch(chooseUserModalActions.setUserType(value as UserType));

      if (!value) {
        dispatch(chooseUserModalActions.resetModalState());
      }
    },
    [dispatch],
  );

  const selectedUserInfo = selectedUser?.firstName
    ? `${selectedUser.firstName} ${selectedUser?.lastName || ''}`
    : selectedUser?.email
    ? selectedUser.email
    : selectedUser?.phone;

  return (
    <Form form={form} layout="vertical" onFinish={handleSubmit} className="flex flex-col px-10 py-4">
      <div className="flex justify-between items-center py-2">
        <div className="font-semibold text-3xl">{t(isEditMode ? 'Edit' : 'Create', { ns: 'common' })}</div>
        <div className="text-primaryLight font-normal cursor-pointer" onClick={onClose}>
          {t('Close')}
        </div>
      </div>

      {isEditMode && (
        <div className="flex items-center mb-3">
          <div className="flex-1">
            <div>{t('Created by')}</div>
            <div className="font-semibold text-xl">
              {waitingListEntry?.createdBy ? `${waitingListEntry.createdBy.firstName} ${waitingListEntry.createdBy.lastName}` : t('System')}
            </div>
          </div>

          <Form.Item name="isCompleted" valuePropName="checked" className="flex-2">
            <Checkbox name="isCompleted" className="mt-6">
              {t('Completed')}
            </Checkbox>
          </Form.Item>
        </div>
      )}

      <Form.Item label={t('User type')} name="userType">
        <Select options={userTypeOptions} onChange={changeUserType} placeholder={t('Please, select user type')} bordered allowClear />
      </Form.Item>

      {selectedUser && (
        <Form.Item label={t('Selected user')}>
          <Input value={selectedUserInfo} bordered />
        </Form.Item>
      )}

      <Form.Item label={t('Warehouse')} name="warehouseIds">
        <MultipleSelect className="w-full" placeholder={t('All')} bordered options={warehouseOptions} allowClear />
      </Form.Item>

      <Form.Item label={t('Type')} name="unitType" rules={[{ required: true, message: `${t('Please, select type')}!` }]}>
        <Select options={unitTypeOptions} placeholder={t('Please, select type')} bordered allowClear />
      </Form.Item>

      <Form.Item label={t('Square')} name="square">
        <RangeInput
          values={[square?.[0], square?.[1]]}
          placeholder={[t('From'), t('To')]}
          type="number"
          onChangeFrom={(v) => {
            form.setFieldsValue({ square: [v, square?.[1] ?? null] });
          }}
          onChangeTo={(v) => {
            form.setFieldsValue({ square: [square?.[0] ?? null, v] });
          }}
        />
      </Form.Item>

      <Form.Item className="w-full" name="preferredDate" label={t('Preferred date')}>
        <DatePicker
          className="h-[52px] w-full"
          format={CLIENT_DATE_FORMAT}
          placeholder={t('Enter preferred date')}
          inputReadOnly
          disabledDate={disableDate}
        />
      </Form.Item>

      <Form.Item label={t('Description')} name="description">
        <TextArea placeholder={t('Add description...')} bordered rows={3} />
      </Form.Item>

      <Button type="submit" isLoading={isLoading}>
        {t('Save', { ns: 'common' })}
      </Button>

      {isEditMode && (
        <div className="mt-6">
          <div className="mb-2">{t('Descriptions history')}:</div>
          <Paragraph expandable={false} className="whitespace-normal">
            {paragraphElements}
          </Paragraph>
        </div>
      )}
    </Form>
  );
});
